import { WarningIcon } from '@c/icons';
import Button from '@ui/Button';

interface QueryErrorStateProps {
  /** What failed to load, in the user's words: "purchases", "listings". */
  subject: string;
  onRetry: () => void;
  isRetrying?: boolean;
  /** Overrides for states that are not a failed read — see the variants below. */
  title?: string;
  description?: string;
  retryLabel?: string;
  /**
   * Offer a way to reach a human. Opt-in, and deliberately NOT the default:
   * this component is shared by six dashboard surfaces, and a transient
   * "something went wrong, try again" does not warrant sending everyone to
   * the support inbox. Turn it on for dead ends the user cannot retry their
   * way out of — a permission-denied order, for instance, where retrying
   * will fail identically every time.
   */
  showSupportLink?: boolean;
}

/** The app's one support channel (Navbar.tsx, policy pages). Not a new one. */
export const SUPPORT_EMAIL = 'support@gearfocus.com';

/**
 * Shown when a dashboard page cannot state what the user has.
 *
 * The reason this exists: on these pages a failed load used to render the same
 * markup as a genuinely empty account, so a seller with 40 listings saw "No
 * Listings Found" and had no way to tell a broken read from an empty one. An
 * error must look like an error and offer a retry that does not require a full
 * page reload.
 */
const QueryErrorState = ({
  subject,
  onRetry,
  isRetrying = false,
  title,
  description,
  retryLabel = 'Try again',
  showSupportLink = false,
}: QueryErrorStateProps) => (
  <div
    role="alert"
    className="flex h-full w-full flex-col items-center justify-center gap-[2.4rem] self-stretch py-[4rem]"
  >
    <div className="flex flex-col items-center justify-center gap-[1.6rem] text-center">
      <WarningIcon />
      <h2 className="text-[2.4rem] font-semibold leading-[3.2rem] text-brand-light-black">
        {title ?? `We couldn't load your ${subject}`}
      </h2>
      <span className="max-w-[40rem] text-brand-dark-gray">
        {description ??
          'Something went wrong on our end. This is not a change to your account.'}
      </span>
      <Button
        type="secondary"
        width="small"
        text={isRetrying ? 'Retrying' : retryLabel}
        onClick={() => onRetry()}
        loading={isRetrying}
        disabled={isRetrying}
      />
      {showSupportLink ? (
        <span className="text-brand-dark-gray">
          Still stuck?{' '}
          <a
            href={`mailto:${SUPPORT_EMAIL}`}
            className="text-brand-secondary underline"
          >
            Contact support
          </a>
        </span>
      ) : null}
    </div>
  </div>
);

/**
 * Auth has been unresolved long enough that "loading" is no longer an honest
 * description. The user-document listener stays silent when Firestore is
 * unreachable, so without this the page would show a skeleton forever.
 */
export const AuthStalledState = ({
  subject,
  onRetry,
}: {
  subject: string;
  onRetry: () => void;
}) => (
  <QueryErrorState
    subject={subject}
    onRetry={onRetry}
    title="This is taking longer than it should"
    description={`We haven't been able to confirm your account, so we can't show your ${subject} yet.`}
    retryLabel="Try again"
  />
);

/**
 * Auth resolved to nobody. Middleware normally redirects first, but it fails
 * open on timeout and is skipped on preview builds — and showing "$0.00" or
 * "No Purchases" to someone we have not identified is a false statement.
 */
export const SignedOutState = ({ subject }: { subject: string }) => (
  <QueryErrorState
    subject={subject}
    onRetry={() => {
      window.location.href = `/login?redirect=${encodeURIComponent(
        window.location.pathname + window.location.search
      )}`;
    }}
    title="Sign in to see this"
    description={`You need to be signed in to view your ${subject}.`}
    retryLabel="Sign in"
  />
);

export default QueryErrorState;
